import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GetInteractionsRequest } from "../../api/interaction";
import { useInteractionMetadata } from "../../hooks/useInteraction";
import Interaction, {
  InteractionType,
} from "../../types/interaction/Interaction";
import SearchResults, { SEARCH_DEFAULT_SORT_MODEL } from "./SearchResults";
import { isEqual } from "lodash";
import SearchDrawer from "./SearchDrawer";
import {
  DEFAULT_SEARCH_VALUES,
  SearchFormValues,
} from "../../types/search/SearchFormValues";
import SearchFieldLabelProvider from "./context/SearchFieldLabelProvider";
import { Badge, Box, Button } from "@mui/material";
import { MediaDrawer } from "../../components/media/MediaDrawer";
import useScreenSize from "../../hooks/useScreenSize";
import FieldLabelApiStorageProvider from "./context/FieldLabelApiStorageProvider";
import { FilterAlt } from "@mui/icons-material";
import { useSearch } from "../../hooks/useSearch";
import BulkActionsToolbar from "./components/bulk-actions/BulkActionsToolbar";
import { AuthContext } from "../../context/AuthContext";
import {
  saveRowsPerPageToLocalStorage,
  getRowsPerPageFromLocalStorage,
} from "../../helpers/rowsPerPage";
import { convertToRequest } from "../../helpers/search/searchHelpers";
import { useTranslation } from "react-i18next";

export const DEFAULT_REQUEST: GetInteractionsRequest = {
  pageIndex: 0,
  pageSize: 100,
};

const getPreferredMediaWidth = (defaultMediaWidth: number): number => {
  const preferredMediaWidth = localStorage.getItem("prefMediaPlayerWidth");
  if (preferredMediaWidth) {
    return Number(preferredMediaWidth);
  }
  return defaultMediaWidth;
};

export const SEARCH_TABLE_ID = "interactions";

const Search = ({ ...props }) => {
  const { t } = useTranslation(["search", "common"]);
  const { viewId } = props;
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [playerInteractionId, setPlayerInteractionId] = useState<string>("");
  const [sortModel, setSortModel] = useState<GridSortModel>(
    SEARCH_DEFAULT_SORT_MODEL
  );
  const [rowCount, setRowCount] = useState<number>(0);

  const getInitialPaginationModel = () => {
    const savedPageSize = getRowsPerPageFromLocalStorage(SEARCH_TABLE_ID);
    return {
      pageIndex: 0,
      pageSize: savedPageSize ?? 100,
    };
  };

  const [paginationModel, setPaginationModel] = useState(
    getInitialPaginationModel
  );

  const [selectedRows, setSelectedRows] = useState<Interaction[]>([]);
  const { customerId } = useParams();
  const [searchRequest, setSearchRequest] =
    useState<GetInteractionsRequest>(DEFAULT_REQUEST);

  const { data, refetch, isRefetching, isLoading } = useSearch(
    customerId,
    viewId,
    searchRequest
  );

  const prevSearchValues = useRef<SearchFormValues | undefined>();
  const [searchValues, setSearchValues] = useState<SearchFormValues>(
    DEFAULT_SEARCH_VALUES
  );
  const [openFilterDrawer, setOpenFilterDrawer] = useState<boolean>(false);
  const [mediaDrawerOpen, setMediaDrawerOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const { width: screenWidth } = useScreenSize();
  const [mediaDrawerWidth, setMediaDrawerWidth] = useState(
    getPreferredMediaWidth(useScreenSize().width * 0.3)
  );
  const minMediaDrawerWidth = screenWidth * 0.2;
  const maxMediaDrawerWidth = screenWidth * 0.5;

  //display bulk actions if user has permissions
  const { user } = useContext(AuthContext);
  const hasBulkActions =
    user?.hasPermission("interactions:Update") ||
    user?.hasPermission("interactions:Export");

  const { data: interactionMetadata } = useInteractionMetadata(
    customerId!,
    playerInteractionId
  );

  const mediaType: string | undefined = interactionMetadata?.interactionType as
    | InteractionType
    | undefined;

  const [filterDrawerWidth, setFilterDrawerWidth] = useState<number>(300);

  useEffect(() => {
    const newMediaDrawerWidth = Math.max(
      minMediaDrawerWidth,
      Math.min(mediaDrawerWidth, maxMediaDrawerWidth)
    );
    setMediaDrawerWidth(newMediaDrawerWidth);
  }, [screenWidth, minMediaDrawerWidth, maxMediaDrawerWidth, mediaDrawerWidth]);

  useEffect(() => {
    setMediaDrawerWidth((prevWidth) =>
      Math.max(minMediaDrawerWidth, Math.min(prevWidth, maxMediaDrawerWidth))
    );
  }, [minMediaDrawerWidth, maxMediaDrawerWidth]);

  useEffect(() => {
    if (data?.totalCount == null) return;
    setRowCount(data.totalCount);
  }, [data]);

  const getUpdatedSearchRequest = useCallback(
    (searchValues: SearchFormValues) => {
      return convertToRequest(
        searchValues,
        paginationModel.pageSize,
        0,
        sortModel
      );
    },
    [paginationModel.pageSize, sortModel]
  );

  useEffect(() => {
    if (!isEqual(searchValues, prevSearchValues.current)) {
      const newRequest = getUpdatedSearchRequest(searchValues);
      setSearchRequest(newRequest);
      prevSearchValues.current = searchValues;
      return;
    }
    setSearchRequest(
      convertToRequest(
        searchValues,
        paginationModel.pageSize,
        paginationModel.pageIndex,
        sortModel
      )
    );
    prevSearchValues.current = searchValues;
  }, [
    paginationModel.pageIndex,
    paginationModel.pageSize,
    searchValues,
    sortModel,
    getUpdatedSearchRequest,
  ]);

  useEffect(() => {
    setInteractions(data?.interactions ?? []);
  }, [data]);

  const handlePaginationModelChange = ({
    page,
    pageSize,
  }: GridPaginationModel) => {
    saveRowsPerPageToLocalStorage(pageSize, SEARCH_TABLE_ID);
    setPaginationModel({ pageIndex: page, pageSize });
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const closePlayer = () => {
    setIsPlaying(false);
    setPlayerInteractionId("");
    setMediaDrawerOpen(false);
  };

  const handleDrawer = () => {
    setOpenFilterDrawer(!openFilterDrawer);
  };

  const getFilterCount = () => {
    const actualFilters = Object.entries(searchValues).filter(
      ([key, value]) => {
        const defaultValue =
          DEFAULT_SEARCH_VALUES[key as keyof SearchFormValues];
        if (value === null) {
          return false;
        } else if (Array.isArray(value) && Array.isArray(defaultValue)) {
          //I'll need this for tags eventually
          return value.length !== defaultValue.length;
        } else {
          return value !== defaultValue;
        }
      }
    );
    return actualFilters.length;
  };

  return (
    <FieldLabelApiStorageProvider customerId={customerId!}>
      <SearchFieldLabelProvider tableId={SEARCH_TABLE_ID}>
        <div
          style={{
            display: "flex",
            height: "100%",
            transition: "all 0.1s",
            marginLeft: openFilterDrawer ? filterDrawerWidth : "0",
            marginRight: mediaDrawerOpen ? mediaDrawerWidth : "0",
          }}
        >
          <SearchDrawer
            setSearchValues={setSearchValues}
            refetch={refetch}
            customerId={customerId!}
            closePlayer={closePlayer}
            filterDrawerWidth={filterDrawerWidth}
            setFilterDrawerWidth={setFilterDrawerWidth}
            openFilterDrawer={openFilterDrawer}
            setOpenFilterDrawer={setOpenFilterDrawer}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            getSearchRequest={getUpdatedSearchRequest}
          />
          <div className="h-full flex flex-col p-4 w-full">
            <Box display={"flex"} justifyContent={"space-between"}>
              <div>
                <Badge badgeContent={getFilterCount()} color="info">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleDrawer}
                    startIcon={<FilterAlt />}
                  >
                    {t("common:filter")}
                  </Button>
                </Badge>
              </div>
              {hasBulkActions && (
                <BulkActionsToolbar
                  interactions={selectedRows}
                  customerId={customerId}
                />
              )}
            </Box>
            <SearchResults
              playerInteractionId={playerInteractionId}
              setPlayerInteractionId={setPlayerInteractionId}
              setSelectedRows={setSelectedRows}
              selectedRows={selectedRows}
              className="flex-1 flex-grow mt-2"
              results={interactions}
              customerId={customerId!}
              isLoading={isLoading ?? isRefetching}
              paginationModel={{
                page: paginationModel.pageIndex,
                pageSize: paginationModel.pageSize,
              }}
              onPaginationModelChange={handlePaginationModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              rowCount={rowCount}
              tableId={SEARCH_TABLE_ID}
              refresh={() => {
                refetch();
              }}
              setMediaDrawerOpen={setMediaDrawerOpen}
            />
            <MediaDrawer
              mediaDrawerOpen={mediaDrawerOpen}
              playerInteractionId={playerInteractionId}
              customerId={customerId!}
              closePlayer={closePlayer}
              isPlaying={isPlaying}
              setIsPlaying={setIsPlaying}
              mediaDrawerWidth={mediaDrawerWidth}
              setMediaDrawerWidth={setMediaDrawerWidth}
              minMediaDrawerWidth={minMediaDrawerWidth}
              maxMediaDrawerWidth={maxMediaDrawerWidth}
              mediaType={mediaType}
            />
          </div>
        </div>
      </SearchFieldLabelProvider>
    </FieldLabelApiStorageProvider>
  );
};

export default Search;
